import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import {
    Plus, Search, Users, Building2, Globe, Mail, Phone, MapPin,
    Package, FolderOpen, Clock, ExternalLink, MessageSquare,
    Palette, Image, Share2, Trash2, Edit3, X, Copy, Check,
    Linkedin, Youtube, Instagram, Facebook, Twitter, Upload, Archive, RotateCcw,
    Download, ImagePlus, Eye, Calendar, BarChart3, KeyRound, LayoutGrid, List, Link as LinkIcon, Pencil,
    Menu, ChevronDown
} from 'lucide-react';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import CustomerPublicPageManager from '../components/CustomerPublicPageManager';
import ContentPlanManager from '../components/ContentPlanManager';
import ReportsManager from '../components/ReportsManager';
import CustomerCredentials from '../components/CustomerCredentials';

// TikTok Icon (not in lucide-react)
const TikTokIcon = ({ className }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
    </svg>
);

const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (hrs > 0) return `${hrs}h ${mins}m`;
    return `${mins}m`;
};

// Helper to get full URL for uploads
const getFileUrl = (path) => {
    if (!path) return null;
    // Handle object with url property (R2 uploads)
    if (typeof path === 'object' && path.url) {
        return path.url;
    }
    // Handle string paths
    if (typeof path !== 'string') return null;
    if (path.startsWith('http')) return path;
    const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
    // Handle both /uploads/... and /api/uploads/... paths
    if (path.startsWith('/uploads/')) {
        return `${baseUrl}/api${path}`;
    }
    return `${baseUrl}${path}`;
};

const CustomersPage = () => {
    const { api, user } = useAuth();
    const [customers, setCustomers] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [copiedColor, setCopiedColor] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [showContentPlan, setShowContentPlan] = useState(null);
    const [showReports, setShowReports] = useState(null);
    const [showCredentials, setShowCredentials] = useState(null);
    const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
    const [activeTab, setActiveTab] = useState('info'); // Tab state for customer detail modal
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false); // Mobile sidebar toggle
    const [showArchived, setShowArchived] = useState(false); // Archive view toggle
    const logoInputRef = useRef(null);
    const createLogoInputRef = useRef(null);
    const editLogoInputRef = useRef(null);
    
    const canManage = user?.role === 'chef' || user?.role === 'account_manager';
    const isAdmin = user?.role === 'chef';
    
    // Customer Links State
    const [customerLinks, setCustomerLinks] = useState([]);
    const [showLinkModal, setShowLinkModal] = useState(false);
    const [editingLink, setEditingLink] = useState(null);
    const [linkForm, setLinkForm] = useState({ emoji: '🔗', name: '', url: '' });
    
    // Package Fields from system settings
    const [packageFields, setPackageFields] = useState([]);

    // Reset active tab when customer changes
    useEffect(() => {
        if (selectedCustomer) {
            setActiveTab('info');
            fetchCustomerLinks(selectedCustomer.id);
        }
    }, [selectedCustomer?.id]);

    // Fetch customer links
    const fetchCustomerLinks = async (customerId) => {
        try {
            const res = await api.get(`/customers/${customerId}/links`);
            setCustomerLinks(res.data || []);
        } catch (e) {
            setCustomerLinks([]);
        }
    };

    // Save/Update customer link
    const handleSaveLink = async () => {
        if (!linkForm.name || !linkForm.url) {
            toast.error('Name und URL sind erforderlich');
            return;
        }
        try {
            if (editingLink) {
                await api.put(`/customers/${selectedCustomer.id}/links/${editingLink.id}`, linkForm);
                toast.success('Link aktualisiert');
            } else {
                await api.post(`/customers/${selectedCustomer.id}/links`, linkForm);
                toast.success('Link hinzugefügt');
            }
            fetchCustomerLinks(selectedCustomer.id);
            setShowLinkModal(false);
            setEditingLink(null);
            setLinkForm({ emoji: '🔗', name: '', url: '' });
        } catch (e) {
            toast.error('Fehler beim Speichern');
        }
    };

    // Delete customer link
    const handleDeleteLink = async (linkId) => {
        try {
            await api.delete(`/customers/${selectedCustomer.id}/links/${linkId}`);
            toast.success('Link gelöscht');
            fetchCustomerLinks(selectedCustomer.id);
        } catch (e) {
            toast.error('Fehler beim Löschen');
        }
    };

    // Load view preference from API on mount
    useEffect(() => {
        const loadPreferences = async () => {
            try {
                const res = await api.get('/users/me/preferences');
                if (res.data?.customers_view) {
                    setViewMode(res.data.customers_view);
                }
            } catch (e) {}
        };
        loadPreferences();
        
        // Load package fields from system settings
        const loadPackageFields = async () => {
            try {
                const res = await api.get('/admin/settings');
                setPackageFields(res.data?.package_fields || [
                    'Postings META',
                    'Postings TikTok',
                    'Stories META',
                    'Fotoproduktion',
                    'HQ Videoproduktion',
                    'UGC Videoproduktion',
                    'Community Management',
                    'Ad Management (exkl. Ad Budget)',
                    'Reporting'
                ]);
            } catch (e) {
                // Use defaults if settings not available
                setPackageFields([
                    'Postings META',
                    'Postings TikTok',
                    'Stories META',
                    'Fotoproduktion',
                    'HQ Videoproduktion',
                    'UGC Videoproduktion',
                    'Community Management',
                    'Ad Management (exkl. Ad Budget)',
                    'Reporting'
                ]);
            }
        };
        loadPackageFields();
    }, [api]);

    // Save view preference when changed
    const handleViewChange = async (newView) => {
        setViewMode(newView);
        try {
            await api.post('/users/me/preferences', { customers_view: newView });
        } catch (e) {}
    };
    
    const [formData, setFormData] = useState({
        name: '',
        website: '',
        email: '',
        phone: '',
        address: '',
        package_name: '',
        package_content: '',
        package_fields: {}, // New: structured package fields
        google_drive_link: '',
        internal_notes: '',
        ci_colors: [''],
        logos: [],
        social_media: {
            linkedin: '',
            youtube: '',
            instagram: '',
            facebook: '',
            tiktok: '',
            twitter: ''
        },
        shared_with: []
    });
    const [createProfileImage, setCreateProfileImage] = useState(null); // Temp profile image for create modal
    const [createProfilePreview, setCreateProfilePreview] = useState(null); // Preview URL
    const [isEditing, setIsEditing] = useState(false);
    const [editData, setEditData] = useState(null);
    const [showPublicPageManager, setShowPublicPageManager] = useState(false);
    const [publicPageCustomer, setPublicPageCustomer] = useState(null);

    const openPublicPageManager = (customer) => {
        setPublicPageCustomer(customer);
        setShowPublicPageManager(true);
    };

    const fetchData = useCallback(async () => {
        try {
            const [customersRes, usersRes] = await Promise.all([
                api.get('/customers'),
                api.get('/users')
            ]);
            setCustomers(customersRes.data);
            setUsers(usersRes.data);
        } catch (error) {
            toast.error('Fehler beim Laden der Daten');
        } finally {
            setLoading(false);
        }
    }, [api]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleCreateCustomer = async () => {
        try {
            const payload = {
                ...formData,
                ci_colors: formData.ci_colors.filter(c => c.trim()),
                social_media: Object.fromEntries(
                    Object.entries(formData.social_media).filter(([_, v]) => v.trim())
                )
            };
            
            const response = await api.post('/customers', payload);
            const newCustomerId = response.data.id;
            
            // Upload profile image if selected
            if (createProfileImage && newCustomerId) {
                const imageFormData = new FormData();
                imageFormData.append('file', createProfileImage);
                try {
                    await api.post(`/customers/${newCustomerId}/profile-image`, imageFormData, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                    });
                } catch (imgError) {
                    console.error('Profile image upload failed:', imgError);
                }
            }
            
            toast.success('Kunde erstellt');
            setShowCreateModal(false);
            resetForm();
            setCreateProfileImage(null);
            setCreateProfilePreview(null);
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler beim Erstellen');
        }
    };

    const handleUpdateCustomer = async (customerId, updates) => {
        try {
            await api.put(`/customers/${customerId}`, updates);
            toast.success('Kunde aktualisiert');
            const res = await api.get(`/customers/${customerId}`);
            setSelectedCustomer(res.data);
            fetchData();
        } catch (error) {
            toast.error('Fehler beim Aktualisieren');
        }
    };

    // Archive/Unarchive customer (Admin only)
    const handleArchiveCustomer = async (customerId, archive = true) => {
        if (!isAdmin) return;
        try {
            await api.put(`/customers/${customerId}/${archive ? 'archive' : 'unarchive'}`);
            toast.success(archive ? 'Kunde archiviert' : 'Kunde wiederhergestellt');
            setSelectedCustomer(null);
            fetchData();
        } catch (error) {
            toast.error('Fehler beim Archivieren');
        }
    };

    // Delete customer (Admin only)
    const handleDeleteCustomer = async (customerId) => {
        if (!isAdmin) return;
        if (!window.confirm('Kunde wirklich ENDGÜLTIG löschen? Diese Aktion kann nicht rückgängig gemacht werden!')) return;
        if (!window.confirm('Bist du WIRKLICH sicher? Alle Daten dieses Kunden werden gelöscht.')) return;
        
        try {
            await api.delete(`/customers/${customerId}`);
            toast.success('Kunde gelöscht');
            setSelectedCustomer(null);
            fetchData();
        } catch (error) {
            toast.error('Fehler beim Löschen');
        }
    };

    // Get placeholder for package field
    const getFieldPlaceholder = (field) => {
        const placeholders = {
            'Postings META': 'z.B. 8/Monat',
            'Postings TikTok': 'z.B. 4/Monat',
            'Stories META': 'z.B. 15/Monat',
            'Fotoproduktion': 'z.B. 1x/Monat',
            'HQ Videoproduktion': 'z.B. 2/Monat',
            'UGC Videoproduktion': 'z.B. 4/Monat',
            'Community Management': 'Ja/Nein',
            'Ad Management (exkl. Ad Budget)': 'Ja/Nein',
            'Ad Budget': 'z.B. 1200€',
            'Reporting': 'z.B. Monatlich'
        };
        return placeholders[field] || 'Wert eingeben';
    };

    const handleLogoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !selectedCustomer) return;

        if (file.size > 5 * 1024 * 1024) {
            toast.error('Datei zu groß (max. 5MB)');
            return;
        }

        setUploading(true);
        const uploadFormData = new FormData();
        uploadFormData.append('file', file);

        try {
            const res = await api.post(`/customers/${selectedCustomer.id}/logos`, uploadFormData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            toast.success('Logo hochgeladen');
            const customerRes = await api.get(`/customers/${selectedCustomer.id}`);
            setSelectedCustomer(customerRes.data);
            fetchData();
        } catch (error) {
            toast.error('Fehler beim Hochladen');
        } finally {
            setUploading(false);
            if (logoInputRef.current) logoInputRef.current.value = '';
        }
    };

    // Upload logo during edit mode
    const handleEditLogoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !selectedCustomer || !editData) return;

        if (file.size > 5 * 1024 * 1024) {
            toast.error('Datei zu groß (max. 5MB)');
            return;
        }

        setUploading(true);
        const uploadFormData = new FormData();
        uploadFormData.append('file', file);

        try {
            const res = await api.post(`/customers/${selectedCustomer.id}/logos`, uploadFormData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            toast.success('Logo hochgeladen');
            // Refresh customer data
            const customerRes = await api.get(`/customers/${selectedCustomer.id}`);
            setSelectedCustomer(customerRes.data);
            fetchData();
        } catch (error) {
            toast.error('Fehler beim Hochladen');
        } finally {
            setUploading(false);
            if (editLogoInputRef.current) editLogoInputRef.current.value = '';
        }
    };

    // Download logo
    const downloadLogo = (logoUrl, customerName, index) => {
        const link = document.createElement('a');
        link.href = logoUrl;
        link.download = `${customerName.replace(/\s+/g, '_')}_logo_${index + 1}.png`;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Delete logo
    const handleDeleteLogo = async (logoUrl) => {
        if (!selectedCustomer || !canManage) return;
        if (!window.confirm('Logo wirklich löschen?')) return;
        
        try {
            const updatedLogos = selectedCustomer.logos.filter(l => l !== logoUrl);
            await api.put(`/customers/${selectedCustomer.id}`, { logos: updatedLogos });
            toast.success('Logo gelöscht');
            const customerRes = await api.get(`/customers/${selectedCustomer.id}`);
            setSelectedCustomer(customerRes.data);
            fetchData();
        } catch (error) {
            toast.error('Fehler beim Löschen');
        }
    };

    const copyColor = (color) => {
        navigator.clipboard.writeText(color);
        setCopiedColor(color);
        toast.success('Farbcode kopiert!');
        setTimeout(() => setCopiedColor(null), 2000);
    };

    const resetForm = () => {
        setFormData({
            name: '',
            website: '',
            email: '',
            phone: '',
            address: '',
            package_name: '',
            package_content: '',
            google_drive_link: '',
            internal_notes: '',
            ci_colors: [''],
            logos: [],
            social_media: { linkedin: '', youtube: '', instagram: '', facebook: '', tiktok: '', twitter: '' },
            shared_with: []
        });
        setCreateProfileImage(null);
        setCreateProfilePreview(null);
    };

    const startEditing = (customer) => {
        setEditData({
            name: customer.name || '',
            website: customer.website || '',
            email: customer.email || '',
            phone: customer.phone || '',
            address: customer.address || '',
            package_name: customer.package_name || '',
            package_content: customer.package_content || '',
            package_fields: customer.package_fields || {},
            google_drive_link: customer.google_drive_link || '',
            internal_notes: customer.internal_notes || '',
            ci_colors: customer.ci_colors?.length ? [...customer.ci_colors] : [''],
            social_media: {
                linkedin: customer.social_media?.linkedin || '',
                youtube: customer.social_media?.youtube || '',
                instagram: customer.social_media?.instagram || '',
                facebook: customer.social_media?.facebook || '',
                tiktok: customer.social_media?.tiktok || '',
                twitter: customer.social_media?.twitter || ''
            },
            shared_with: customer.shared_with || []
        });
        setIsEditing(true);
    };

    const cancelEditing = () => {
        setEditData(null);
        setIsEditing(false);
    };

    const saveEditing = async () => {
        if (!selectedCustomer || !editData) return;
        try {
            const payload = {
                ...editData,
                ci_colors: editData.ci_colors.filter(c => c.trim()),
                social_media: Object.fromEntries(
                    Object.entries(editData.social_media).filter(([_, v]) => v.trim())
                )
            };
            await api.put(`/customers/${selectedCustomer.id}`, payload);
            toast.success('Kunde aktualisiert');
            const res = await api.get(`/customers/${selectedCustomer.id}`);
            setSelectedCustomer(res.data);
            setIsEditing(false);
            setEditData(null);
            fetchData();
        } catch (error) {
            toast.error('Fehler beim Aktualisieren');
        }
    };

    const addEditColor = () => {
        if (!editData) return;
        setEditData({ ...editData, ci_colors: [...editData.ci_colors, ''] });
    };

    const updateEditColor = (index, value) => {
        if (!editData) return;
        const newColors = [...editData.ci_colors];
        newColors[index] = value;
        setEditData({ ...editData, ci_colors: newColors });
    };

    const removeEditColor = (index) => {
        if (!editData) return;
        const newColors = editData.ci_colors.filter((_, i) => i !== index);
        setEditData({ ...editData, ci_colors: newColors.length ? newColors : [''] });
    };

    const addColorField = () => {
        setFormData({ ...formData, ci_colors: [...formData.ci_colors, ''] });
    };

    const updateColor = (index, value) => {
        const newColors = [...formData.ci_colors];
        newColors[index] = value;
        setFormData({ ...formData, ci_colors: newColors });
    };

    const removeColor = (index) => {
        const newColors = formData.ci_colors.filter((_, i) => i !== index);
        setFormData({ ...formData, ci_colors: newColors.length ? newColors : [''] });
    };

    const activeCustomers = customers.filter(c => !c.is_archived);
    const archivedCustomers = customers.filter(c => c.is_archived);
    
    const filteredCustomers = (showArchived ? archivedCustomers : activeCustomers)
        .filter(c =>
            c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            c.email?.toLowerCase().includes(searchQuery.toLowerCase())
        )
        .sort((a, b) => (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase(), 'de'));

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="p-4 md:p-8 max-w-7xl mx-auto" data-testid="customers-page">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        {showArchived ? 'Archivierte Kunden' : 'Kunden'}
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        {activeCustomers.length} aktiv{archivedCustomers.length > 0 ? `, ${archivedCustomers.length} archiviert` : ''}
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    {/* View Toggle */}
                    <div className="flex items-center border border-border rounded-lg overflow-hidden">
                        <button
                            onClick={() => handleViewChange('grid')}
                            className={`p-2 transition-colors ${viewMode === 'grid' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
                            title="Kachelansicht"
                            data-testid="view-grid-btn"
                        >
                            <LayoutGrid className="w-4 h-4" />
                        </button>
                        <button
                            onClick={() => handleViewChange('list')}
                            className={`p-2 transition-colors ${viewMode === 'list' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`}
                            title="Listenansicht"
                            data-testid="view-list-btn"
                        >
                            <List className="w-4 h-4" />
                        </button>
                    </div>
                    {/* Archive toggle */}
                    {archivedCustomers.length > 0 && (
                        <Button
                            variant={showArchived ? "default" : "outline"}
                            size="sm"
                            onClick={() => setShowArchived(!showArchived)}
                            data-testid="toggle-archived"
                        >
                            <Archive className="w-4 h-4 mr-2" />
                            {showArchived ? 'Aktive zeigen' : `Archiv (${archivedCustomers.length})`}
                        </Button>
                    )}
                    <div className="relative flex-1 md:w-64">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                            placeholder="Kunden suchen..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-10"
                            data-testid="customer-search"
                        />
                    </div>
                    {canManage && (
                        <Button onClick={() => setShowCreateModal(true)} data-testid="create-customer-button">
                            <Plus className="w-4 h-4 mr-2" />
                            Neuer Kunde
                        </Button>
                    )}
                </div>
            </div>

            {/* Customer Grid/List View */}
            {viewMode === 'grid' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <AnimatePresence>
                        {filteredCustomers.map((customer, index) => (
                            <motion.div
                                key={customer.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.95 }}
                                transition={{ delay: index * 0.05 }}
                            >
                                <Card
                                    className="cursor-pointer hover:shadow-lg transition-all group"
                                    onClick={() => setSelectedCustomer(customer)}
                                    data-testid={`customer-card-${customer.id}`}
                                >
                                    <CardContent className="p-4">
                                        <div className="flex items-start gap-3">
                                            {/* Logo or Initial */}
                                            <div
                                                className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg shrink-0 overflow-hidden"
                                                style={{ backgroundColor: customer.ci_colors?.[0] || '#009dde' }}
                                            >
                                                {customer.profile_image ? (
                                                    <img src={customer.profile_image} alt="" className="w-full h-full object-cover" />
                                                ) : customer.logos?.[0] ? (
                                                    <img src={getFileUrl(customer.logos[0])} alt="" className="w-full h-full object-cover" />
                                                ) : (
                                                    customer.name?.[0]?.toUpperCase()
                                                )}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <h3 className="font-semibold truncate group-hover:text-primary transition-colors">
                                                    {customer.name}
                                                </h3>
                                                {customer.email && (
                                                    <p className="text-sm text-muted-foreground truncate">{customer.email}</p>
                                                )}
                                                <div className="flex items-center gap-2 mt-2">
                                                    <Badge variant="outline" className="text-xs">
                                                        <FolderOpen className="w-3 h-3 mr-1" />
                                                        {customer.projects?.length || 0} Projekte
                                                    </Badge>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        {/* Time Stats */}
                                        <div className="grid grid-cols-3 gap-2 mt-4 pt-3 border-t border-border/50">
                                            <div className="text-center">
                                                <p className="text-xs text-muted-foreground">Woche</p>
                                                <p className="text-sm font-semibold">{formatTime(customer.time_stats?.week || 0)}</p>
                                            </div>
                                            <div className="text-center">
                                                <p className="text-xs text-muted-foreground">Monat</p>
                                                <p className="text-sm font-semibold">{formatTime(customer.time_stats?.month || 0)}</p>
                                            </div>
                                            <div className="text-center">
                                                <p className="text-xs text-muted-foreground">Gesamt</p>
                                                <p className="text-sm font-semibold">{formatTime(customer.time_stats?.total || 0)}</p>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        ))}
                    </AnimatePresence>
                </div>
            ) : (
                /* List View */
                <div className="space-y-2">
                    <AnimatePresence>
                        {filteredCustomers.map((customer, index) => (
                            <motion.div
                                key={customer.id}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: 20 }}
                                transition={{ delay: index * 0.03 }}
                            >
                                <Card
                                    className="cursor-pointer hover:shadow-md transition-all group"
                                    onClick={() => setSelectedCustomer(customer)}
                                    data-testid={`customer-row-${customer.id}`}
                                >
                                    <CardContent className="p-3 flex items-center gap-4">
                                        {/* Logo or Initial */}
                                        <div
                                            className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold shrink-0 overflow-hidden"
                                            style={{ backgroundColor: customer.ci_colors?.[0] || '#009dde' }}
                                        >
                                            {customer.profile_image ? (
                                                <img src={customer.profile_image} alt="" className="w-full h-full object-cover" />
                                            ) : customer.logos?.[0] ? (
                                                <img src={getFileUrl(customer.logos[0])} alt="" className="w-full h-full object-cover" />
                                            ) : (
                                                customer.name?.[0]?.toUpperCase()
                                            )}
                                        </div>
                                        
                                        {/* Name & Email */}
                                        <div className="flex-1 min-w-0">
                                            <h3 className="font-semibold truncate group-hover:text-primary transition-colors">
                                                {customer.name}
                                            </h3>
                                            {customer.email && (
                                                <p className="text-sm text-muted-foreground truncate">{customer.email}</p>
                                            )}
                                        </div>
                                        
                                        {/* Projects Badge */}
                                        <Badge variant="outline" className="text-xs shrink-0">
                                            <FolderOpen className="w-3 h-3 mr-1" />
                                            {customer.projects?.length || 0}
                                        </Badge>
                                        
                                        {/* Time Stats - Compact */}
                                        <div className="hidden md:flex items-center gap-4 text-sm text-muted-foreground shrink-0">
                                            <div className="text-center">
                                                <p className="text-xs">Woche</p>
                                                <p className="font-medium text-foreground">{formatTime(customer.time_stats?.week || 0)}</p>
                                            </div>
                                            <div className="text-center">
                                                <p className="text-xs">Monat</p>
                                                <p className="font-medium text-foreground">{formatTime(customer.time_stats?.month || 0)}</p>
                                            </div>
                                            <div className="text-center">
                                                <p className="text-xs">Gesamt</p>
                                                <p className="font-medium text-foreground">{formatTime(customer.time_stats?.total || 0)}</p>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        ))}
                    </AnimatePresence>
                </div>
            )}

            {filteredCustomers.length === 0 && (
                <div className="text-center py-12">
                    <Users className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                    <h3 className="text-lg font-medium mb-2">Keine Kunden gefunden</h3>
                    <p className="text-muted-foreground">
                        {searchQuery ? 'Versuche einen anderen Suchbegriff' : 'Erstelle deinen ersten Kunden'}
                    </p>
                </div>
            )}

            {/* Create Customer Modal */}
            <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden" data-testid="create-customer-modal">
                    <DialogHeader>
                        <DialogTitle>Neuen Kunden erstellen</DialogTitle>
                    </DialogHeader>
                    <ScrollArea className="max-h-[60vh] pr-4">
                        <div className="space-y-4">
                            {/* Basic Info */}
                            <div className="grid grid-cols-2 gap-4">
                                <div className="col-span-2">
                                    <Label>Kundenname *</Label>
                                    <Input
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        placeholder="Firmenname"
                                        data-testid="customer-name-input"
                                    />
                                </div>
                                <div>
                                    <Label>Website</Label>
                                    <Input
                                        value={formData.website}
                                        onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                                        placeholder="https://example.com"
                                    />
                                </div>
                                <div>
                                    <Label>E-Mail</Label>
                                    <Input
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                        placeholder="kontakt@example.com"
                                    />
                                </div>
                                <div>
                                    <Label>Telefon</Label>
                                    <Input
                                        value={formData.phone}
                                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                        placeholder="+43 1 234 5678"
                                    />
                                </div>
                                <div>
                                    <Label>Adresse</Label>
                                    <Input
                                        value={formData.address}
                                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                                        placeholder="Straße, Stadt"
                                    />
                                </div>
                            </div>

                            {/* Package Fields */}
                            <div className="pt-4 border-t border-border/50">
                                <h4 className="font-medium mb-3 flex items-center gap-2">
                                    <Package className="w-4 h-4" />
                                    Paket-Leistungen
                                </h4>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    {packageFields.map((field) => (
                                        <div key={field} className="flex items-center gap-2">
                                            <Label className="text-sm text-muted-foreground min-w-[140px] truncate">{field}</Label>
                                            <Input
                                                value={formData.package_fields?.[field] || ''}
                                                onChange={(e) => setFormData({ 
                                                    ...formData, 
                                                    package_fields: { 
                                                        ...formData.package_fields, 
                                                        [field]: e.target.value 
                                                    }
                                                })}
                                                placeholder="z.B. 8/Monat"
                                                className="flex-1"
                                            />
                                        </div>
                                    ))}
                                </div>
                                {packageFields.length === 0 && (
                                    <p className="text-sm text-muted-foreground">Keine Paket-Felder konfiguriert. Gehe zu System → Einstellungen.</p>
                                )}
                            </div>

                            {/* CI Colors */}
                            <div className="pt-4 border-t border-border/50">
                                <h4 className="font-medium mb-3 flex items-center gap-2">
                                    <Palette className="w-4 h-4" />
                                    CI Farben
                                </h4>
                                <div className="space-y-2">
                                    {formData.ci_colors.map((color, index) => (
                                        <div key={index} className="flex items-center gap-2">
                                            <input
                                                type="color"
                                                value={color || '#009dde'}
                                                onChange={(e) => updateColor(index, e.target.value)}
                                                className="w-10 h-10 rounded cursor-pointer"
                                            />
                                            <Input
                                                value={color}
                                                onChange={(e) => updateColor(index, e.target.value)}
                                                placeholder="#000000"
                                                className="flex-1"
                                            />
                                            {formData.ci_colors.length > 1 && (
                                                <Button variant="ghost" size="icon" onClick={() => removeColor(index)}>
                                                    <X className="w-4 h-4" />
                                                </Button>
                                            )}
                                        </div>
                                    ))}
                                    <Button variant="outline" size="sm" onClick={addColorField}>
                                        <Plus className="w-4 h-4 mr-1" />
                                        Farbe hinzufügen
                                    </Button>
                                </div>
                            </div>

                            {/* Social Media */}
                            <div className="pt-4 border-t border-border/50">
                                <h4 className="font-medium mb-3 flex items-center gap-2">
                                    <Share2 className="w-4 h-4" />
                                    Social Media
                                </h4>
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="flex items-center gap-2">
                                        <Linkedin className="w-4 h-4 text-muted-foreground shrink-0" />
                                        <Input
                                            value={formData.social_media.linkedin}
                                            onChange={(e) => setFormData({
                                                ...formData,
                                                social_media: { ...formData.social_media, linkedin: e.target.value }
                                            })}
                                            placeholder="LinkedIn URL"
                                        />
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Youtube className="w-4 h-4 text-muted-foreground shrink-0" />
                                        <Input
                                            value={formData.social_media.youtube}
                                            onChange={(e) => setFormData({
                                                ...formData,
                                                social_media: { ...formData.social_media, youtube: e.target.value }
                                            })}
                                            placeholder="YouTube URL"
                                        />
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Instagram className="w-4 h-4 text-muted-foreground shrink-0" />
                                        <Input
                                            value={formData.social_media.instagram}
                                            onChange={(e) => setFormData({
                                                ...formData,
                                                social_media: { ...formData.social_media, instagram: e.target.value }
                                            })}
                                            placeholder="Instagram URL"
                                        />
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Facebook className="w-4 h-4 text-muted-foreground shrink-0" />
                                        <Input
                                            value={formData.social_media.facebook}
                                            onChange={(e) => setFormData({
                                                ...formData,
                                                social_media: { ...formData.social_media, facebook: e.target.value }
                                            })}
                                            placeholder="Facebook URL"
                                        />
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <TikTokIcon className="w-4 h-4 text-muted-foreground shrink-0" />
                                        <Input
                                            value={formData.social_media.tiktok}
                                            onChange={(e) => setFormData({
                                                ...formData,
                                                social_media: { ...formData.social_media, tiktok: e.target.value }
                                            })}
                                            placeholder="TikTok URL"
                                        />
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Twitter className="w-4 h-4 text-muted-foreground shrink-0" />
                                        <Input
                                            value={formData.social_media.twitter}
                                            onChange={(e) => setFormData({
                                                ...formData,
                                                social_media: { ...formData.social_media, twitter: e.target.value }
                                            })}
                                            placeholder="X (Twitter) URL"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Files & Notes */}
                            <div className="pt-4 border-t border-border/50">
                                <div className="space-y-4">
                                    <div>
                                        <Label>Google Drive Link</Label>
                                        <Input
                                            value={formData.google_drive_link}
                                            onChange={(e) => setFormData({ ...formData, google_drive_link: e.target.value })}
                                            placeholder="https://drive.google.com/..."
                                        />
                                    </div>
                                    <div>
                                        <Label>Interner Kommentar</Label>
                                        <Textarea
                                            value={formData.internal_notes}
                                            onChange={(e) => setFormData({ ...formData, internal_notes: e.target.value })}
                                            placeholder="Interne Notizen zum Kunden..."
                                            rows={3}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Profile Image */}
                            <div className="pt-4 border-t border-border/50">
                                <h4 className="font-medium mb-3 flex items-center gap-2">
                                    <Users className="w-4 h-4" />
                                    Profilfoto
                                </h4>
                                <div className="flex items-center gap-4">
                                    <div className="relative">
                                        {createProfilePreview ? (
                                            <div className="w-16 h-16 rounded-full overflow-hidden bg-muted/50 ring-2 ring-primary/20">
                                                <img src={createProfilePreview} alt="" className="w-full h-full object-cover" />
                                            </div>
                                        ) : (
                                            <div className="w-16 h-16 rounded-full border-2 border-dashed border-border/50 flex items-center justify-center bg-muted/30">
                                                <Users className="w-5 h-5 text-muted-foreground" />
                                            </div>
                                        )}
                                    </div>
                                    <div className="flex flex-col gap-2">
                                        <input
                                            type="file"
                                            id="create-profile-image"
                                            className="hidden"
                                            accept="image/*"
                                            onChange={(e) => {
                                                const file = e.target.files?.[0];
                                                if (file) {
                                                    if (file.size > 5 * 1024 * 1024) {
                                                        toast.error('Datei zu groß (max. 5MB)');
                                                        return;
                                                    }
                                                    setCreateProfileImage(file);
                                                    setCreateProfilePreview(URL.createObjectURL(file));
                                                }
                                                e.target.value = '';
                                            }}
                                        />
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={() => document.getElementById('create-profile-image')?.click()}
                                        >
                                            <Upload className="w-4 h-4 mr-2" />
                                            {createProfilePreview ? 'Ändern' : 'Hochladen'}
                                        </Button>
                                        {createProfilePreview && (
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => {
                                                    setCreateProfileImage(null);
                                                    setCreateProfilePreview(null);
                                                }}
                                            >
                                                <X className="w-4 h-4 mr-1" />
                                                Entfernen
                                            </Button>
                                        )}
                                    </div>
                                </div>
                                <p className="text-xs text-muted-foreground mt-2">Wird als Avatar und auf der öffentlichen Kundenseite angezeigt</p>
                            </div>

                            {/* Share with employees */}
                            <div className="pt-4 border-t border-border/50">
                                <h4 className="font-medium mb-3 flex items-center gap-2">
                                    <Users className="w-4 h-4" />
                                    Kunden freigeben
                                </h4>
                                <Select
                                    value=""
                                    onValueChange={(v) => {
                                        if (v && !formData.shared_with.includes(v)) {
                                            setFormData({ ...formData, shared_with: [...formData.shared_with, v] });
                                        }
                                    }}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Mitarbeiter hinzufügen..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {users.filter(u => !formData.shared_with.includes(u.id)).map(u => (
                                            <SelectItem key={u.id} value={u.id}>
                                                {u.first_name} {u.last_name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                {formData.shared_with.length > 0 && (
                                    <div className="flex flex-wrap gap-2 mt-2">
                                        {formData.shared_with.map(userId => {
                                            const u = users.find(x => x.id === userId);
                                            return u ? (
                                                <Badge key={userId} variant="secondary" className="gap-1">
                                                    {u.first_name} {u.last_name}
                                                    <button
                                                        onClick={() => setFormData({
                                                            ...formData,
                                                            shared_with: formData.shared_with.filter(id => id !== userId)
                                                        })}
                                                    >
                                                        <X className="w-3 h-3" />
                                                    </button>
                                                </Badge>
                                            ) : null;
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    </ScrollArea>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => { setShowCreateModal(false); resetForm(); }}>
                            Abbrechen
                        </Button>
                        <Button onClick={handleCreateCustomer} disabled={!formData.name} data-testid="submit-customer-button">
                            Erstellen
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Customer Detail Modal */}
            <AnimatePresence>
                {selectedCustomer && (
                    <Dialog open={!!selectedCustomer} onOpenChange={() => { setSelectedCustomer(null); cancelEditing(); setMobileMenuOpen(false); }}>
                        <DialogContent className="max-w-5xl w-[95vw] max-h-[90vh] overflow-hidden p-0 [&>button]:hidden !top-[5vh] !translate-y-0 md:!top-[50%] md:!translate-y-[-50%]" data-testid="customer-detail-modal">
                            <div className="flex h-full flex-col md:flex-row">
                                {/* Mobile Header with Menu Toggle - with safe-area padding for PWA */}
                                <div className="md:hidden flex items-center justify-between px-4 py-3 pt-[max(1rem,calc(env(safe-area-inset-top)+0.5rem))] border-b border-border/50 bg-muted/30">
                                    <div className="flex items-center gap-3">
                                        <div
                                            className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold shrink-0 overflow-hidden"
                                            style={{ backgroundColor: selectedCustomer.ci_colors?.[0] || '#009dde' }}
                                        >
                                            {selectedCustomer.profile_image ? (
                                                <img src={selectedCustomer.profile_image} alt="" className="w-full h-full object-cover" />
                                            ) : selectedCustomer.logos?.[0] ? (
                                                <img src={getFileUrl(selectedCustomer.logos[0])} alt="" className="w-full h-full object-cover" />
                                            ) : (
                                                selectedCustomer.name?.[0]?.toUpperCase()
                                            )}
                                        </div>
                                        <div className="min-w-0">
                                            <h2 className="font-semibold truncate text-sm">{selectedCustomer.name}</h2>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                                            className="flex items-center gap-2"
                                        >
                                            <Menu className="w-4 h-4" />
                                            Menü
                                            <ChevronDown className={`w-4 h-4 transition-transform ${mobileMenuOpen ? 'rotate-180' : ''}`} />
                                        </Button>
                                        <Button variant="ghost" size="icon" onClick={() => { setSelectedCustomer(null); cancelEditing(); }}>
                                            <X className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                                
                                {/* Mobile Dropdown Menu */}
                                <AnimatePresence>
                                    {mobileMenuOpen && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }}
                                            animate={{ height: 'auto', opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }}
                                            className="md:hidden bg-muted/30 border-b border-border/50 overflow-hidden"
                                        >
                                            <div className="p-4 pt-3 grid grid-cols-2 gap-2.5">
                                                <button
                                                    onClick={() => { setActiveTab('info'); setMobileMenuOpen(false); }}
                                                    className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm ${activeTab === 'info' ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted'}`}
                                                >
                                                    <span>📊</span> Info
                                                </button>
                                                {canManage && (
                                                    <>
                                                        <button
                                                            onClick={() => { setActiveTab('publicPage'); setMobileMenuOpen(false); }}
                                                            className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm ${activeTab === 'publicPage' ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted'}`}
                                                        >
                                                            <span>🌐</span> Seite
                                                        </button>
                                                        <button
                                                            onClick={() => { setActiveTab('credentials'); setMobileMenuOpen(false); }}
                                                            className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm ${activeTab === 'credentials' ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted'}`}
                                                        >
                                                            <span>🔐</span> Zugänge
                                                        </button>
                                                        <button
                                                            onClick={() => { setActiveTab('contentPlan'); setMobileMenuOpen(false); }}
                                                            className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm ${activeTab === 'contentPlan' ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted'}`}
                                                        >
                                                            <span>📅</span> Content
                                                        </button>
                                                        <button
                                                            onClick={() => { setActiveTab('reports'); setMobileMenuOpen(false); }}
                                                            className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm ${activeTab === 'reports' ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted'}`}
                                                        >
                                                            <span>📈</span> Reports
                                                        </button>
                                                        <button
                                                            onClick={() => { setActiveTab('links'); setMobileMenuOpen(false); }}
                                                            className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm ${activeTab === 'links' ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted'}`}
                                                        >
                                                            <span>🔗</span> Links
                                                            {customerLinks.length > 0 && <Badge variant="secondary" className="text-xs ml-auto">{customerLinks.length}</Badge>}
                                                        </button>
                                                        <button
                                                            onClick={() => { setActiveTab('edit'); setMobileMenuOpen(false); }}
                                                            className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm ${activeTab === 'edit' ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted'}`}
                                                        >
                                                            <span>✏️</span> Bearbeiten
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                                
                                {/* Desktop Left Sidebar Navigation */}
                                <div className="hidden md:flex w-56 bg-muted/30 border-r border-border/50 flex-col shrink-0">
                                    {/* Customer Header in Sidebar */}
                                    <div className="p-4 border-b border-border/50">
                                        <div className="flex items-center gap-3">
                                            <div
                                                className="w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold shrink-0 overflow-hidden"
                                                style={{ backgroundColor: selectedCustomer.ci_colors?.[0] || '#009dde' }}
                                            >
                                                {selectedCustomer.profile_image ? (
                                                    <img src={selectedCustomer.profile_image} alt="" className="w-full h-full object-cover" />
                                                ) : selectedCustomer.logos?.[0] ? (
                                                    <img src={getFileUrl(selectedCustomer.logos[0])} alt="" className="w-full h-full object-cover" />
                                                ) : (
                                                    selectedCustomer.name?.[0]?.toUpperCase()
                                                )}
                                            </div>
                                            <div className="min-w-0">
                                                <h2 className="font-semibold truncate text-sm">{selectedCustomer.name}</h2>
                                                {selectedCustomer.package_name && (
                                                    <Badge variant="outline" className="text-xs mt-1">
                                                        {selectedCustomer.package_name}
                                                    </Badge>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Navigation Menu */}
                                    <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
                                        <div className="text-xs font-medium text-muted-foreground px-2 py-2">Übersicht</div>
                                        
                                        <button
                                            onClick={() => setActiveTab('info')}
                                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left ${activeTab === 'info' ? 'bg-primary/10 text-primary' : 'hover:bg-muted'}`}
                                            data-testid="nav-overview"
                                        >
                                            <span className="text-lg">📊</span>
                                            Kunden-Info
                                        </button>
                                        
                                        {canManage && (
                                            <>
                                                <div className="text-xs font-medium text-muted-foreground px-2 py-2 mt-4">Tools & Aktionen</div>
                                                
                                                <button
                                                    onClick={() => setActiveTab('publicPage')}
                                                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors text-left ${activeTab === 'publicPage' ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted'}`}
                                                    data-testid="nav-public-page"
                                                >
                                                    <span className="text-lg">🌐</span>
                                                    Kundenseite
                                                </button>
                                                
                                                <button
                                                    onClick={() => setActiveTab('credentials')}
                                                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors text-left ${activeTab === 'credentials' ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted'}`}
                                                    data-testid="nav-credentials"
                                                >
                                                    <span className="text-lg">🔐</span>
                                                    Zugänge
                                                </button>
                                                
                                                <button
                                                    onClick={() => setActiveTab('contentPlan')}
                                                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors text-left ${activeTab === 'contentPlan' ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted'}`}
                                                    data-testid="nav-content-plan"
                                                >
                                                    <span className="text-lg">📅</span>
                                                    Content-Plan
                                                </button>
                                                
                                                <button
                                                    onClick={() => setActiveTab('reports')}
                                                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors text-left ${activeTab === 'reports' ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted'}`}
                                                    data-testid="nav-reports"
                                                >
                                                    <span className="text-lg">📈</span>
                                                    Reportings
                                                </button>
                                                
                                                {selectedCustomer.google_drive_link && (
                                                    <a
                                                        href={selectedCustomer.google_drive_link}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm hover:bg-muted transition-colors"
                                                    >
                                                        <span className="text-lg">📁</span>
                                                        Kunden Files
                                                        <ExternalLink className="w-3 h-3 ml-auto text-muted-foreground" />
                                                    </a>
                                                )}
                                                
                                                <button
                                                    onClick={() => setActiveTab('links')}
                                                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors text-left ${activeTab === 'links' ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted'}`}
                                                    data-testid="nav-links"
                                                >
                                                    <span className="text-lg">🔗</span>
                                                    Links
                                                    {customerLinks.length > 0 && (
                                                        <Badge variant="secondary" className="ml-auto text-xs">{customerLinks.length}</Badge>
                                                    )}
                                                </button>
                                                
                                                <div className="text-xs font-medium text-muted-foreground px-2 py-2 mt-4">Verwaltung</div>
                                                
                                                <button
                                                    onClick={() => setActiveTab('edit')}
                                                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors text-left ${activeTab === 'edit' ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-muted'}`}
                                                    data-testid="nav-edit"
                                                >
                                                    <span className="text-lg">✏️</span>
                                                    Bearbeiten
                                                </button>
                                                
                                                {isAdmin && (
                                                    <>
                                                        {selectedCustomer.is_archived ? (
                                                            <button
                                                                onClick={() => handleArchiveCustomer(selectedCustomer.id, false)}
                                                                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm hover:bg-muted transition-colors text-left text-green-500"
                                                                data-testid="nav-unarchive"
                                                            >
                                                                <span className="text-lg">♻️</span>
                                                                Wiederherstellen
                                                            </button>
                                                        ) : (
                                                            <button
                                                                onClick={() => handleArchiveCustomer(selectedCustomer.id, true)}
                                                                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm hover:bg-muted transition-colors text-left"
                                                                data-testid="nav-archive"
                                                            >
                                                                <span className="text-lg">📦</span>
                                                                Archivieren
                                                            </button>
                                                        )}
                                                        
                                                        <button
                                                            onClick={() => handleDeleteCustomer(selectedCustomer.id)}
                                                            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm hover:bg-destructive/10 transition-colors text-left text-destructive"
                                                            data-testid="nav-delete"
                                                        >
                                                            <span className="text-lg">🗑️</span>
                                                            Löschen
                                                        </button>
                                                    </>
                                                )}
                                            </>
                                        )}
                                    </nav>
                                    
                                    {/* Close Button at Bottom */}
                                    <div className="p-3 border-t border-border/50">
                                        <Button 
                                            variant="outline" 
                                            className="w-full"
                                            onClick={() => { setSelectedCustomer(null); cancelEditing(); }}
                                        >
                                            <X className="w-4 h-4 mr-2" />
                                            Schließen
                                        </Button>
                                    </div>
                                </div>
                                
                                {/* Main Content Area */}
                                <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
                                    {/* Header - Hidden on mobile since we have the mobile header */}
                                    <div className="hidden md:flex items-center justify-between p-4 border-b border-border/50">
                                        <div>
                                            <h2 className="text-xl font-semibold">{selectedCustomer.name}</h2>
                                            <div className="flex items-center gap-2 mt-1">
                                                {selectedCustomer.package_name && (
                                                    <Badge variant="outline">
                                                        <Package className="w-3 h-3 mr-1" />
                                                        {selectedCustomer.package_name}
                                                    </Badge>
                                                )}
                                                {selectedCustomer.is_archived && (
                                                    <Badge variant="secondary" className="bg-yellow-500/20 text-yellow-600">
                                                        Archiviert
                                                    </Badge>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                            {isEditing && editData ? (
                                /* EDIT MODE */
                                <>
                                    <ScrollArea className="max-h-[60vh]">
                                        <div className="p-4 space-y-4">
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <Label>Name *</Label>
                                                    <Input 
                                                        value={editData.name} 
                                                        onChange={(e) => setEditData({...editData, name: e.target.value})}
                                                    />
                                                </div>
                                                <div>
                                                    <Label>Website</Label>
                                                    <Input 
                                                        value={editData.website} 
                                                        onChange={(e) => setEditData({...editData, website: e.target.value})}
                                                        placeholder="https://..."
                                                    />
                                                </div>
                                                <div>
                                                    <Label>E-Mail</Label>
                                                    <Input 
                                                        type="email"
                                                        value={editData.email} 
                                                        onChange={(e) => setEditData({...editData, email: e.target.value})}
                                                    />
                                                </div>
                                                <div>
                                                    <Label>Telefon</Label>
                                                    <Input 
                                                        value={editData.phone} 
                                                        onChange={(e) => setEditData({...editData, phone: e.target.value})}
                                                    />
                                                </div>
                                                <div className="col-span-2">
                                                    <Label>Adresse</Label>
                                                    <Input 
                                                        value={editData.address} 
                                                        onChange={(e) => setEditData({...editData, address: e.target.value})}
                                                    />
                                                </div>
                                                <div className="col-span-2">
                                                    <Label>Google Drive Link</Label>
                                                    <Input 
                                                        value={editData.google_drive_link} 
                                                        onChange={(e) => setEditData({...editData, google_drive_link: e.target.value})}
                                                    />
                                                </div>
                                                
                                                {/* Package Fields */}
                                                <div className="col-span-2 pt-4 border-t border-border/50">
                                                    <Label className="mb-3 block">Paket-Leistungen</Label>
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                        {packageFields.map((field) => (
                                                            <div key={field} className="flex items-center gap-2">
                                                                <Label className="text-xs text-muted-foreground min-w-[120px] truncate">{field}</Label>
                                                                <Input
                                                                    value={editData.package_fields?.[field] || ''}
                                                                    onChange={(e) => setEditData({ 
                                                                        ...editData, 
                                                                        package_fields: { 
                                                                            ...editData.package_fields, 
                                                                            [field]: e.target.value 
                                                                        }
                                                                    })}
                                                                    placeholder={getFieldPlaceholder(field)}
                                                                    className="flex-1 h-8 text-sm"
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                                
                                                <div className="col-span-2">
                                                    <Label>Interner Kommentar</Label>
                                                    <Textarea 
                                                        value={editData.internal_notes} 
                                                        onChange={(e) => setEditData({...editData, internal_notes: e.target.value})}
                                                        rows={2}
                                                    />
                                                </div>
                                            </div>

                                            {/* Profile Image Upload - Prominent position */}
                                            <div className="border rounded-lg p-4 bg-muted/30">
                                                <Label className="text-base font-medium">Profilfoto</Label>
                                                <p className="text-xs text-muted-foreground mb-3">Wird als Avatar und auf der öffentlichen Kundenseite angezeigt</p>
                                                <div className="flex items-center gap-4">
                                                    <div className="relative group">
                                                        {selectedCustomer.profile_image ? (
                                                            <div className="w-20 h-20 rounded-full overflow-hidden bg-muted/50 ring-2 ring-primary/20">
                                                                <img 
                                                                    src={selectedCustomer.profile_image} 
                                                                    alt="" 
                                                                    className="w-full h-full object-cover" 
                                                                />
                                                                <Button 
                                                                    variant="destructive" 
                                                                    size="icon" 
                                                                    className="absolute -top-1 -right-1 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                                                                    onClick={async () => {
                                                                        try {
                                                                            await api.delete(`/customers/${selectedCustomer.id}/profile-image`);
                                                                            toast.success('Profilfoto gelöscht');
                                                                            const res = await api.get(`/customers/${selectedCustomer.id}`);
                                                                            setSelectedCustomer(res.data);
                                                                        } catch (error) {
                                                                            toast.error('Fehler beim Löschen');
                                                                        }
                                                                    }}
                                                                >
                                                                    <X className="w-3 h-3" />
                                                                </Button>
                                                            </div>
                                                        ) : (
                                                            <div className="w-20 h-20 rounded-full border-2 border-dashed border-border/50 flex items-center justify-center bg-muted/50">
                                                                <Users className="w-6 h-6 text-muted-foreground" />
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <input
                                                            type="file"
                                                            id="profile-image-upload"
                                                            className="hidden"
                                                            accept="image/*"
                                                            onChange={async (e) => {
                                                                const file = e.target.files?.[0];
                                                                if (!file || !selectedCustomer) return;
                                                                if (file.size > 5 * 1024 * 1024) {
                                                                    toast.error('Datei zu groß (max. 5MB)');
                                                                    return;
                                                                }
                                                                setUploading(true);
                                                                const formData = new FormData();
                                                                formData.append('file', file);
                                                                try {
                                                                    await api.post(`/customers/${selectedCustomer.id}/profile-image`, formData, {
                                                                        headers: { 'Content-Type': 'multipart/form-data' }
                                                                    });
                                                                    toast.success('Profilfoto hochgeladen');
                                                                    const res = await api.get(`/customers/${selectedCustomer.id}`);
                                                                    setSelectedCustomer(res.data);
                                                                } catch (error) {
                                                                    toast.error('Fehler beim Hochladen');
                                                                } finally {
                                                                    setUploading(false);
                                                                    e.target.value = '';
                                                                }
                                                            }}
                                                        />
                                                        <Button
                                                            onClick={() => document.getElementById('profile-image-upload')?.click()}
                                                            disabled={uploading}
                                                            className="gap-2"
                                                        >
                                                            {uploading ? (
                                                                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                                            ) : (
                                                                <Upload className="w-4 h-4" />
                                                            )}
                                                            {selectedCustomer.profile_image ? 'Foto ändern' : 'Foto hochladen'}
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* CI Colors */}
                                            <div>
                                                <Label>CI-Farben</Label>
                                                <div className="space-y-2 mt-2">
                                                    {editData.ci_colors.map((color, index) => (
                                                        <div key={index} className="flex items-center gap-2">
                                                            <input
                                                                type="color"
                                                                value={color || '#009dde'}
                                                                onChange={(e) => updateEditColor(index, e.target.value)}
                                                                className="w-10 h-10 rounded cursor-pointer"
                                                            />
                                                            <Input
                                                                value={color}
                                                                onChange={(e) => updateEditColor(index, e.target.value)}
                                                                placeholder="#009dde"
                                                                className="flex-1"
                                                            />
                                                            {editData.ci_colors.length > 1 && (
                                                                <Button variant="ghost" size="icon" onClick={() => removeEditColor(index)}>
                                                                    <X className="w-4 h-4" />
                                                                </Button>
                                                            )}
                                                        </div>
                                                    ))}
                                                    <Button variant="outline" size="sm" onClick={addEditColor}>
                                                        <Plus className="w-4 h-4 mr-2" /> Farbe hinzufügen
                                                    </Button>
                                                </div>
                                            </div>

                                            {/* Social Media */}
                                            <div>
                                                <Label>Social Media</Label>
                                                <div className="grid grid-cols-2 gap-3 mt-2">
                                                    <div className="flex items-center gap-2">
                                                        <Linkedin className="w-4 h-4 text-muted-foreground" />
                                                        <Input
                                                            value={editData.social_media.linkedin}
                                                            onChange={(e) => setEditData({
                                                                ...editData,
                                                                social_media: {...editData.social_media, linkedin: e.target.value}
                                                            })}
                                                            placeholder="LinkedIn URL"
                                                        />
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <Instagram className="w-4 h-4 text-muted-foreground" />
                                                        <Input
                                                            value={editData.social_media.instagram}
                                                            onChange={(e) => setEditData({
                                                                ...editData,
                                                                social_media: {...editData.social_media, instagram: e.target.value}
                                                            })}
                                                            placeholder="Instagram URL"
                                                        />
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <Facebook className="w-4 h-4 text-muted-foreground" />
                                                        <Input
                                                            value={editData.social_media.facebook}
                                                            onChange={(e) => setEditData({
                                                                ...editData,
                                                                social_media: {...editData.social_media, facebook: e.target.value}
                                                            })}
                                                            placeholder="Facebook URL"
                                                        />
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <Youtube className="w-4 h-4 text-muted-foreground" />
                                                        <Input
                                                            value={editData.social_media.youtube}
                                                            onChange={(e) => setEditData({
                                                                ...editData,
                                                                social_media: {...editData.social_media, youtube: e.target.value}
                                                            })}
                                                            placeholder="YouTube URL"
                                                        />
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <TikTokIcon className="w-4 h-4 text-muted-foreground" />
                                                        <Input
                                                            value={editData.social_media.tiktok}
                                                            onChange={(e) => setEditData({
                                                                ...editData,
                                                                social_media: {...editData.social_media, tiktok: e.target.value}
                                                            })}
                                                            placeholder="TikTok URL"
                                                        />
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <Twitter className="w-4 h-4 text-muted-foreground" />
                                                        <Input
                                                            value={editData.social_media.twitter}
                                                            onChange={(e) => setEditData({
                                                                ...editData,
                                                                social_media: {...editData.social_media, twitter: e.target.value}
                                                            })}
                                                            placeholder="X (Twitter) URL"
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Logos Upload */}
                                            <div>
                                                <Label>Logos</Label>
                                                <div className="flex flex-wrap gap-3 mt-2">
                                                    {selectedCustomer.logos?.map((logo, i) => (
                                                        <div key={i} className="relative group">
                                                            <div className="w-16 h-16 rounded-lg bg-muted/50 p-1">
                                                                <img src={getFileUrl(logo)} alt="" className="w-full h-full object-contain" />
                                                            </div>
                                                            <Button 
                                                                variant="destructive" 
                                                                size="icon" 
                                                                className="absolute -top-2 -right-2 h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity"
                                                                onClick={() => handleDeleteLogo(logo)}
                                                            >
                                                                <X className="w-3 h-3" />
                                                            </Button>
                                                        </div>
                                                    ))}
                                                    <button
                                                        onClick={() => editLogoInputRef.current?.click()}
                                                        className="w-16 h-16 rounded-lg border-2 border-dashed border-border/50 flex items-center justify-center hover:border-primary/50 transition-colors"
                                                        disabled={uploading}
                                                    >
                                                        {uploading ? (
                                                            <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                                                        ) : (
                                                            <ImagePlus className="w-4 h-4 text-muted-foreground" />
                                                        )}
                                                    </button>
                                                </div>
                                                <input
                                                    ref={editLogoInputRef}
                                                    type="file"
                                                    className="hidden"
                                                    accept="image/*"
                                                    onChange={handleEditLogoUpload}
                                                />
                                            </div>

                                            {/* Shared With */}
                                            <div>
                                                <Label>Sichtbar für</Label>
                                                <Select
                                                    value=""
                                                    onValueChange={(v) => {
                                                        if (v && !editData.shared_with.includes(v)) {
                                                            setEditData({...editData, shared_with: [...editData.shared_with, v]});
                                                        }
                                                    }}
                                                >
                                                    <SelectTrigger className="mt-2">
                                                        <SelectValue placeholder="Mitarbeiter hinzufügen..." />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {users.filter(u => !editData.shared_with.includes(u.id) && u.id !== user?.id).map(u => (
                                                            <SelectItem key={u.id} value={u.id}>
                                                                {u.first_name} {u.last_name}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                                {editData.shared_with.length > 0 && (
                                                    <div className="flex flex-wrap gap-2 mt-2">
                                                        {editData.shared_with.map(userId => {
                                                            const u = users.find(x => x.id === userId);
                                                            return u ? (
                                                                <Badge key={userId} variant="secondary" className="gap-1">
                                                                    {u.first_name} {u.last_name}
                                                                    <button onClick={() => setEditData({
                                                                        ...editData,
                                                                        shared_with: editData.shared_with.filter(id => id !== userId)
                                                                    })}>
                                                                        <X className="w-3 h-3" />
                                                                    </button>
                                                                </Badge>
                                                            ) : null;
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </ScrollArea>
                                    <div className="flex justify-end gap-2 p-4 border-t border-border/50">
                                        <Button variant="outline" onClick={cancelEditing}>Abbrechen</Button>
                                        <Button onClick={saveEditing} disabled={!editData.name}>Speichern</Button>
                                    </div>
                                </>
                            ) : activeTab === 'info' ? (
                                /* INFO TAB - Main Customer View */
                                <ScrollArea className="max-h-[65vh]">
                                <div className="p-5 space-y-8">
                                    {/* Contact Info */}
                                    <div className="grid grid-cols-2 gap-4">
                                        {selectedCustomer.website && (
                                            <a 
                                                href={selectedCustomer.website} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary"
                                            >
                                                <Globe className="w-4 h-4" />
                                                {selectedCustomer.website.replace(/^https?:\/\//, '')}
                                                <ExternalLink className="w-3 h-3" />
                                            </a>
                                        )}
                                        {selectedCustomer.email && (
                                            <a 
                                                href={`mailto:${selectedCustomer.email}`}
                                                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary"
                                            >
                                                <Mail className="w-4 h-4" />
                                                {selectedCustomer.email}
                                            </a>
                                        )}
                                        {selectedCustomer.phone && (
                                            <a 
                                                href={`tel:${selectedCustomer.phone}`}
                                                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary"
                                            >
                                                <Phone className="w-4 h-4" />
                                                {selectedCustomer.phone}
                                            </a>
                                        )}
                                        {selectedCustomer.address && (
                                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                                <MapPin className="w-4 h-4" />
                                                {selectedCustomer.address}
                                            </div>
                                        )}
                                    </div>

                                    {/* Time Stats */}
                                    <div className="p-4 rounded-xl bg-muted/50">
                                        <h4 className="font-medium mb-3 flex items-center gap-2">
                                            <Clock className="w-4 h-4" />
                                            Zeiterfassung
                                        </h4>
                                        <div className="grid grid-cols-3 gap-4">
                                            <div className="text-center p-3 rounded-lg bg-background">
                                                <p className="text-xs text-muted-foreground mb-1">Diese Woche</p>
                                                <p className="text-xl font-semibold">{formatTime(selectedCustomer.time_stats?.week || 0)}</p>
                                            </div>
                                            <div className="text-center p-3 rounded-lg bg-background">
                                                <p className="text-xs text-muted-foreground mb-1">Dieser Monat</p>
                                                <p className="text-xl font-semibold">{formatTime(selectedCustomer.time_stats?.month || 0)}</p>
                                            </div>
                                            <div className="text-center p-3 rounded-lg bg-background">
                                                <p className="text-xs text-muted-foreground mb-1">Insgesamt</p>
                                                <p className="text-xl font-semibold">{formatTime(selectedCustomer.time_stats?.total || 0)}</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Projects */}
                                    {selectedCustomer.projects?.length > 0 && (
                                        <div>
                                            <h4 className="font-medium mb-3 flex items-center gap-2">
                                                <FolderOpen className="w-4 h-4" />
                                                Projekte ({selectedCustomer.projects.length})
                                            </h4>
                                            <div className="space-y-2">
                                                {selectedCustomer.projects.map(project => (
                                                    <div
                                                        key={project.id}
                                                        className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 hover:bg-muted cursor-pointer transition-colors"
                                                        onClick={() => window.location.href = `/projects?id=${project.id}`}
                                                    >
                                                        <div 
                                                            className="w-3 h-3 rounded-full"
                                                            style={{ backgroundColor: project.primary_color || '#009dde' }}
                                                        />
                                                        <span className="font-medium">{project.name}</span>
                                                        <Badge variant="outline" className="ml-auto text-xs">
                                                            {project.status === 'active' ? 'Aktiv' : project.status}
                                                        </Badge>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Divider before Kunden Files */}
                                    {selectedCustomer.google_drive_link && (
                                        <div className="border-t border-border/50 my-2" />
                                    )}

                                    {/* Google Drive Link */}
                                    {selectedCustomer.google_drive_link && (
                                        <a
                                            href={selectedCustomer.google_drive_link}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center gap-3 p-4 rounded-xl bg-blue-500/10 border border-blue-500/30 hover:bg-blue-500/20 transition-colors"
                                        >
                                            <FolderOpen className="w-5 h-5 text-blue-500" />
                                            <span className="font-medium">Kunden Files</span>
                                            <ExternalLink className="w-4 h-4 ml-auto text-blue-500" />
                                        </a>
                                    )}

                                    {/* Divider */}
                                    <div className="border-t border-border/50 my-2" />

                                    {/* CI Colors */}
                                    {selectedCustomer.ci_colors?.length > 0 && selectedCustomer.ci_colors[0] && (
                                        <div>
                                            <h4 className="font-medium mb-3 flex items-center gap-2">
                                                <Palette className="w-4 h-4" />
                                                CI Farben
                                            </h4>
                                            <div className="flex flex-wrap gap-2">
                                                {selectedCustomer.ci_colors.filter(c => c).map((color, i) => (
                                                    <button
                                                        key={i}
                                                        onClick={() => copyColor(color)}
                                                        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/50 hover:bg-muted transition-colors group"
                                                    >
                                                        <div 
                                                            className="w-6 h-6 rounded"
                                                            style={{ backgroundColor: color }}
                                                        />
                                                        <span className="text-sm font-mono">{color}</span>
                                                        {copiedColor === color ? (
                                                            <Check className="w-4 h-4 text-green-500" />
                                                        ) : (
                                                            <Copy className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                                                        )}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Divider before Logos */}
                                    <div className="border-t border-border/50 my-2" />

                                    {/* Logos */}
                                    <div>
                                        <h4 className="font-medium mb-3 flex items-center gap-2">
                                            <Image className="w-4 h-4" />
                                            Logos {selectedCustomer.logos?.length > 0 && `(${selectedCustomer.logos.length})`}
                                        </h4>
                                        <div className="flex flex-wrap gap-3">
                                            {selectedCustomer.logos?.map((logo, i) => (
                                                <div key={i} className="relative group">
                                                    <div className="w-20 h-20 rounded-lg bg-muted/50 p-2">
                                                        <img src={getFileUrl(logo)} alt="" className="w-full h-full object-contain" />
                                                    </div>
                                                    {/* Overlay with actions */}
                                                    <div className="absolute inset-0 bg-black/60 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                                                        <Button 
                                                            variant="ghost" 
                                                            size="icon" 
                                                            className="h-7 w-7 text-white hover:bg-white/20"
                                                            onClick={() => downloadLogo(getFileUrl(logo), selectedCustomer.name, i)}
                                                            title="Herunterladen"
                                                        >
                                                            <Download className="w-4 h-4" />
                                                        </Button>
                                                        {canManage && (
                                                            <Button 
                                                                variant="ghost" 
                                                                size="icon" 
                                                                className="h-7 w-7 text-white hover:bg-red-500/50"
                                                                onClick={() => handleDeleteLogo(logo)}
                                                                title="Löschen"
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </Button>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                            {canManage && (
                                                <button
                                                    onClick={() => logoInputRef.current?.click()}
                                                    className="w-20 h-20 rounded-lg border-2 border-dashed border-border/50 flex items-center justify-center hover:border-primary/50 transition-colors"
                                                    disabled={uploading}
                                                >
                                                    {uploading ? (
                                                        <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                                                    ) : (
                                                        <Upload className="w-5 h-5 text-muted-foreground" />
                                                    )}
                                                </button>
                                            )}
                                        </div>
                                        <input
                                            ref={logoInputRef}
                                            type="file"
                                            className="hidden"
                                            accept="image/*"
                                            onChange={handleLogoUpload}
                                        />
                                    </div>

                                    {/* Divider before Social Media */}
                                    {selectedCustomer.social_media && Object.values(selectedCustomer.social_media).some(v => v) && (
                                        <div className="border-t border-border/50 my-2" />
                                    )}

                                    {/* Social Media */}
                                    {selectedCustomer.social_media && Object.values(selectedCustomer.social_media).some(v => v) && (
                                        <div>
                                            <h4 className="font-medium mb-3 flex items-center gap-2">
                                                <Share2 className="w-4 h-4" />
                                                Social Media
                                            </h4>
                                            <div className="flex flex-wrap gap-2">
                                                {selectedCustomer.social_media.linkedin && (
                                                    <a href={selectedCustomer.social_media.linkedin} target="_blank" rel="noopener noreferrer"
                                                        className="p-2 rounded-lg bg-muted/50 hover:bg-[#0077b5]/20 transition-colors">
                                                        <Linkedin className="w-5 h-5" />
                                                    </a>
                                                )}
                                                {selectedCustomer.social_media.youtube && (
                                                    <a href={selectedCustomer.social_media.youtube} target="_blank" rel="noopener noreferrer"
                                                        className="p-2 rounded-lg bg-muted/50 hover:bg-red-500/20 transition-colors">
                                                        <Youtube className="w-5 h-5" />
                                                    </a>
                                                )}
                                                {selectedCustomer.social_media.instagram && (
                                                    <a href={selectedCustomer.social_media.instagram} target="_blank" rel="noopener noreferrer"
                                                        className="p-2 rounded-lg bg-muted/50 hover:bg-pink-500/20 transition-colors">
                                                        <Instagram className="w-5 h-5" />
                                                    </a>
                                                )}
                                                {selectedCustomer.social_media.facebook && (
                                                    <a href={selectedCustomer.social_media.facebook} target="_blank" rel="noopener noreferrer"
                                                        className="p-2 rounded-lg bg-muted/50 hover:bg-blue-500/20 transition-colors">
                                                        <Facebook className="w-5 h-5" />
                                                    </a>
                                                )}
                                                {selectedCustomer.social_media.tiktok && (
                                                    <a href={selectedCustomer.social_media.tiktok} target="_blank" rel="noopener noreferrer"
                                                        className="p-2 rounded-lg bg-muted/50 hover:bg-black/20 transition-colors">
                                                        <TikTokIcon className="w-5 h-5" />
                                                    </a>
                                                )}
                                                {selectedCustomer.social_media.twitter && (
                                                    <a href={selectedCustomer.social_media.twitter} target="_blank" rel="noopener noreferrer"
                                                        className="p-2 rounded-lg bg-muted/50 hover:bg-black/20 transition-colors">
                                                        <Twitter className="w-5 h-5" />
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* Divider before Package Fields */}
                                    {(selectedCustomer.package_fields && Object.keys(selectedCustomer.package_fields).some(k => selectedCustomer.package_fields[k])) && (
                                        <div className="border-t border-border/50 my-2" />
                                    )}

                                    {/* Package Fields - Structured View */}
                                    {selectedCustomer.package_fields && Object.keys(selectedCustomer.package_fields).some(k => selectedCustomer.package_fields[k]) && (
                                        <div>
                                            <h4 className="font-medium mb-3 flex items-center gap-2">
                                                <Package className="w-4 h-4" />
                                                Paket-Leistungen
                                            </h4>
                                            <div className="grid grid-cols-2 gap-2">
                                                {Object.entries(selectedCustomer.package_fields)
                                                    .filter(([_, value]) => value)
                                                    .map(([key, value]) => (
                                                        <div key={key} className="flex items-center justify-between p-2 rounded-lg bg-muted/50">
                                                            <span className="text-sm text-muted-foreground truncate">{key}</span>
                                                            <span className="text-sm font-medium ml-2">{value}</span>
                                                        </div>
                                                    ))
                                                }
                                            </div>
                                        </div>
                                    )}

                                    {/* Internal Notes */}
                                    {selectedCustomer.internal_notes && (
                                        <div className="p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/30">
                                            <h4 className="font-medium mb-2 flex items-center gap-2">
                                                <MessageSquare className="w-4 h-4" />
                                                Interner Kommentar
                                            </h4>
                                            <p className="text-sm whitespace-pre-wrap">{selectedCustomer.internal_notes}</p>
                                        </div>
                                    )}

                                    {/* Shared With - Compact View */}
                                    {selectedCustomer.shared_users?.length > 0 && (
                                        <div>
                                            <h4 className="font-medium mb-3 flex items-center gap-2">
                                                <Users className="w-4 h-4" />
                                                Freigegeben für ({selectedCustomer.shared_users.length})
                                            </h4>
                                            <div className="flex items-center gap-1">
                                                {/* Show first 5 avatars */}
                                                {selectedCustomer.shared_users.slice(0, 5).map(u => (
                                                    <Avatar key={u.id} className="w-8 h-8 border-2 border-background -ml-2 first:ml-0" title={`${u.first_name} ${u.last_name}`}>
                                                        <AvatarFallback style={{ backgroundColor: u.profile_color }} className="text-white text-xs">
                                                            {u.first_name?.[0]}{u.last_name?.[0]}
                                                        </AvatarFallback>
                                                    </Avatar>
                                                ))}
                                                {/* Show +N if more than 5 */}
                                                {selectedCustomer.shared_users.length > 5 && (
                                                    <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center text-xs font-medium -ml-2 border-2 border-background">
                                                        +{selectedCustomer.shared_users.length - 5}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </ScrollArea>
                            ) : activeTab === 'edit' ? (
                                /* EDIT TAB - Trigger edit mode */
                                <ScrollArea className="max-h-[65vh] w-full">
                                    <div className="p-6 space-y-6">
                                        {!isEditing && (
                                            <div className="text-center py-4">
                                                <Edit3 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                                                <h3 className="text-lg font-medium mb-2">Kunde bearbeiten</h3>
                                                <p className="text-muted-foreground mb-4">Klicke auf den Button um die Bearbeitung zu starten</p>
                                                <Button onClick={() => startEditing(selectedCustomer)}>
                                                    <Edit3 className="w-4 h-4 mr-2" />
                                                    Bearbeitung starten
                                                </Button>
                                            </div>
                                        )}
                                        
                                        {/* Admin Actions - Archive & Delete */}
                                        {isAdmin && (
                                            <div className="border-t pt-6 mt-6">
                                                <h4 className="text-sm font-medium text-muted-foreground mb-4">Admin-Aktionen</h4>
                                                <div className="space-y-3">
                                                    {/* Archive/Unarchive Button */}
                                                    {selectedCustomer.is_archived ? (
                                                        <Button
                                                            variant="outline"
                                                            className="w-full justify-start"
                                                            onClick={() => handleArchiveCustomer(selectedCustomer.id, false)}
                                                        >
                                                            <RotateCcw className="w-4 h-4 mr-2" />
                                                            Kunde wiederherstellen
                                                        </Button>
                                                    ) : (
                                                        <Button
                                                            variant="outline"
                                                            className="w-full justify-start"
                                                            onClick={() => handleArchiveCustomer(selectedCustomer.id, true)}
                                                        >
                                                            <Archive className="w-4 h-4 mr-2" />
                                                            Kunde archivieren
                                                        </Button>
                                                    )}
                                                    
                                                    {/* Delete Button */}
                                                    <Button
                                                        variant="outline"
                                                        className="w-full justify-start text-destructive hover:text-destructive hover:bg-destructive/10"
                                                        onClick={() => handleDeleteCustomer(selectedCustomer.id)}
                                                    >
                                                        <Trash2 className="w-4 h-4 mr-2" />
                                                        Kunde endgültig löschen
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </ScrollArea>
                            ) : activeTab === 'publicPage' ? (
                                /* PUBLIC PAGE TAB */
                                <ScrollArea className="max-h-[65vh] w-full">
                                    <div className="p-1 overflow-x-hidden">
                                        <CustomerPublicPageManager 
                                            customer={selectedCustomer}
                                            onClose={() => setActiveTab('info')}
                                            embedded={true}
                                        />
                                    </div>
                                </ScrollArea>
                            ) : activeTab === 'credentials' ? (
                                /* CREDENTIALS TAB */
                                <ScrollArea className="max-h-[65vh] w-full">
                                    <div className="p-1 overflow-x-hidden">
                                        <CustomerCredentials 
                                            customer={selectedCustomer}
                                            onClose={() => setActiveTab('info')}
                                            embedded={true}
                                        />
                                    </div>
                                </ScrollArea>
                            ) : activeTab === 'contentPlan' ? (
                                /* CONTENT PLAN TAB */
                                <ScrollArea className="max-h-[65vh] w-full">
                                    <div className="p-1 overflow-x-hidden">
                                        <ContentPlanManager 
                                            customer={selectedCustomer}
                                            onClose={() => setActiveTab('info')}
                                            embedded={true}
                                        />
                                    </div>
                                </ScrollArea>
                            ) : activeTab === 'reports' ? (
                                /* REPORTS TAB */
                                <ScrollArea className="max-h-[65vh] w-full">
                                    <div className="p-1 overflow-x-hidden">
                                        <ReportsManager 
                                            customer={selectedCustomer}
                                            onClose={() => setActiveTab('info')}
                                            embedded={true}
                                        />
                                    </div>
                                </ScrollArea>
                            ) : activeTab === 'links' ? (
                                /* LINKS TAB */
                                <ScrollArea className="max-h-[65vh] w-full">
                                    <div className="p-6">
                                        <div className="flex items-center justify-between mb-6">
                                            <div>
                                                <h2 className="text-xl font-semibold">Kunden-Links</h2>
                                                <p className="text-sm text-muted-foreground">Schnellzugriff auf wichtige Ressourcen</p>
                                            </div>
                                            <Button onClick={() => { setLinkForm({ emoji: '🔗', name: '', url: '' }); setEditingLink(null); setShowLinkModal(true); }}>
                                                <Plus className="w-4 h-4 mr-2" />
                                                Neuer Link
                                            </Button>
                                        </div>
                                        
                                        {customerLinks.length === 0 ? (
                                            <div className="text-center py-12 text-muted-foreground">
                                                <LinkIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                                <p>Noch keine Links vorhanden</p>
                                                <p className="text-sm">Füge Links zu Canva, Google Drive, etc. hinzu</p>
                                            </div>
                                        ) : (
                                            <div className="grid gap-3">
                                                {customerLinks.map((link) => (
                                                    <div key={link.id} className="flex items-center gap-3 p-4 rounded-xl border bg-card hover:shadow-md transition-all group">
                                                        <span className="text-2xl">{link.emoji || '🔗'}</span>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="font-medium truncate">{link.name}</p>
                                                            <a href={link.url} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline truncate block">
                                                                {link.url}
                                                            </a>
                                                        </div>
                                                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setEditingLink(link); setLinkForm({ emoji: link.emoji || '🔗', name: link.name, url: link.url }); setShowLinkModal(true); }}>
                                                                <Pencil className="w-4 h-4" />
                                                            </Button>
                                                            <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => handleDeleteLink(link.id)}>
                                                                <Trash2 className="w-4 h-4" />
                                                            </Button>
                                                        </div>
                                                        <a href={link.url} target="_blank" rel="noopener noreferrer">
                                                            <Button variant="outline" size="sm">
                                                                <ExternalLink className="w-4 h-4 mr-1" />
                                                                Öffnen
                                                            </Button>
                                                        </a>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </ScrollArea>
                            ) : null}
                                </div>
                            </div>
                        </DialogContent>
                    </Dialog>
                )}

                {/* Public Page Manager Modal */}
                {showPublicPageManager && publicPageCustomer && (
                    <Dialog open={showPublicPageManager} onOpenChange={(open) => {
                        if (!open) {
                            setShowPublicPageManager(false);
                            setPublicPageCustomer(null);
                        }
                    }}>
                        <DialogContent className="max-w-5xl w-[95vw] max-h-[90vh] overflow-y-auto">
                            <CustomerPublicPageManager 
                                customer={publicPageCustomer}
                                onClose={() => {
                                    setShowPublicPageManager(false);
                                    setPublicPageCustomer(null);
                                }}
                            />
                        </DialogContent>
                    </Dialog>
                )}

                {/* Content Plan Modal */}
                {showContentPlan && (
                    <Dialog open={!!showContentPlan} onOpenChange={(open) => {
                        if (!open) setShowContentPlan(null);
                    }}>
                        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
                            <ContentPlanManager 
                                customer={showContentPlan}
                                onClose={() => setShowContentPlan(null)}
                            />
                        </DialogContent>
                    </Dialog>
                )}

                {/* Reports Modal */}
                {showReports && (
                    <Dialog open={!!showReports} onOpenChange={(open) => {
                        if (!open) setShowReports(null);
                    }}>
                        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
                            <ReportsManager 
                                customer={showReports}
                                onClose={() => setShowReports(null)}
                            />
                        </DialogContent>
                    </Dialog>
                )}

                {/* Credentials Modal */}
                {showCredentials && (
                    <CustomerCredentials 
                        customer={showCredentials}
                        onClose={() => setShowCredentials(null)}
                    />
                )}

                {/* Link Add/Edit Modal */}
                <Dialog open={showLinkModal} onOpenChange={setShowLinkModal}>
                    <DialogContent className="max-w-md w-[95vw] mx-auto">
                        <DialogHeader>
                            <DialogTitle>{editingLink ? 'Link bearbeiten' : 'Neuen Link hinzufügen'}</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                            <div>
                                <Label>Emoji</Label>
                                <div className="flex flex-wrap gap-2 mt-1">
                                    {['🔗', '📁', '🎨', '📊', '📝', '🌐', '💼', '📧', '📷', '🎬'].map((emoji) => (
                                        <button
                                            key={emoji}
                                            type="button"
                                            onClick={() => setLinkForm({ ...linkForm, emoji })}
                                            className={`w-9 h-9 sm:w-10 sm:h-10 rounded-lg text-lg sm:text-xl flex items-center justify-center transition-all ${linkForm.emoji === emoji ? 'bg-primary/20 ring-2 ring-primary' : 'bg-muted hover:bg-muted/80'}`}
                                        >
                                            {emoji}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <Label>Name *</Label>
                                <Input
                                    value={linkForm.name}
                                    onChange={(e) => setLinkForm({ ...linkForm, name: e.target.value })}
                                    placeholder="z.B. Canva Grafiken"
                                />
                            </div>
                            <div>
                                <Label>URL *</Label>
                                <Input
                                    value={linkForm.url}
                                    onChange={(e) => setLinkForm({ ...linkForm, url: e.target.value })}
                                    placeholder="https://..."
                                />
                            </div>
                        </div>
                        <DialogFooter className="flex-col sm:flex-row gap-2">
                            <Button variant="outline" onClick={() => setShowLinkModal(false)} className="w-full sm:w-auto">Abbrechen</Button>
                            <Button onClick={handleSaveLink} className="w-full sm:w-auto">{editingLink ? 'Speichern' : 'Hinzufügen'}</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </AnimatePresence>
        </div>
    );
};

export default CustomersPage;
